/**
 * RefreshButton — shared refresh control with consistent UX.
 *
 * Why: every page had its own <Button><RefreshCw/></Button> with its
 * own `loading` flag wiring (or, more often, none — clicking Refresh
 * silently re-fetched with no visible feedback while the network call
 * was in flight, which users misread as "the button is broken").
 *
 * This component:
 *   - Owns its own busy state so the page doesn't need a loading flag
 *     just for the refresh affordance.
 *   - Spins the icon while the onClick promise is pending.
 *   - Disables itself during the call to prevent double-clicks.
 *   - Accepts either a sync or async onClick — wraps in Promise.resolve.
 *   - Falls back to "Refresh" label; pass `label={false}` for icon-only.
 */
import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function RefreshButton({
  onClick,
  label = "Refresh",
  size = "sm",
  variant = "outline",
  className = "",
  disabled = false,
  title,
  ...rest
}) {
  const [busy, setBusy] = useState(false);

  const handleClick = async (e) => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      await Promise.resolve(onClick?.(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={busy || disabled}
      className={className}
      title={title || (busy ? "Refreshing…" : "Refresh")}
      {...rest}
    >
      <RefreshCw className={`w-4 h-4 ${label ? "mr-2" : ""} ${busy ? "animate-spin" : ""}`} />
      {label || null}
    </Button>
  );
}
